import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  Wrapper,
  Header,
  Title,
  Footer,
  SeeMoreButton,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableBodyRow,
  TableBodyCell,
  NoDataAvailable,
  SeeEpisodesButtonWrapper,
} from '@insights_module/components/overview_screen/TrendingStyledComponents';
import { SearchBar } from '@insights_module/components/SearchBar';
import { DateRange, useOverviewContext } from '@insights_module/context/OverviewContext';
import { Loader } from '@components/Loader';
import { Modal } from '@components/Modal';
import usePagination from '@app/hooks/usePagination';
import { DateIntervalSelection } from '@insights_module/components/overview_screen/DateIntervalSelection';
import { Button } from '@components/Button';
import { Pagination } from '@components/Pagination';
import { RankedNamedEntitySummary, useOverviewData } from '@app/hooks/useOverviewData';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { objectToToken } from '@utils/commonUtils';
import {
  AQLLinkedNamedEntitiesFilterQuery,
  AQLLinkedNamedEntitiesQuery,
  AQLPublishedDateQuery,
} from '@insights_module/adc_ql/queries';
import { OpenInNewTabIcon } from '@icons/OpenInNewTabIcon';

// listed only the relevant named entity types
const NAMED_ENTITY_TYPE = [
  'EVENT',
  'FAC',
  'GPE',
  'LANGUAGE',
  'LAW',
  'LOC',
  'MONEY',
  'NORP',
  'ORG',
  'PERSON',
  'PRODUCT',
  'WORK_OF_ART',
];

const entitiesTypeOptions = [
  {
    label: (
      <Option>
        <Trans i18nKey='insights.overview_page.trending_section.top_entities.all_types'>
          All Types
        </Trans>
      </Option>
    ),
    value: 'all',
    hasDivider: true,
  },
  ...NAMED_ENTITY_TYPE.map((type) => ({
    label: (
      <Option>
        <Trans i18nKey={`insights.overview_page.trending_section.top_entities.types.${type}`}>
          {type}
        </Trans>
      </Option>
    ),
    value: type,
  })),
] as IDropdownOption[];

export const TrendingTopNamedEntitiesList = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(entitiesTypeOptions[0]);
  const { episodesCount, entitiesSearchTerm, setEntitiesSearchTerm, setSelectedEntitiesType } =
    useOverviewContext();
  const { top10NamedEntities, rankedNamedEntities, isLoadingNamedEntities } = useOverviewData();

  useEffect(() => {
    if (selectedType.value === 'all') {
      setSelectedEntitiesType('');
      return;
    }

    setSelectedEntitiesType(selectedType.value as string);
  }, [selectedType]);

  const {
    currentPage,
    paginationRange,
    currentPageData,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    totalPages,
  } = usePagination<RankedNamedEntitySummary>({
    data: rankedNamedEntities,
    itemsPerPage: 19,
    filter: JSON.stringify({ entitiesSearchTerm, selectedType }),
  });

  const TypeDropdown = () => (
    <Dropdown
      options={entitiesTypeOptions}
      value={selectedType}
      onChange={(val) => setSelectedType(val as IDropdownOption)}
      dropdownMenuStyle={tw`w-[235px] max-h-[280px] text-text-and-icon`}
      labelStyle={tw`shadow-none text-text-and-icon`}
      tw='max-w-[235px]'
    />
  );

  const CategoriesModal = (
    <Modal tw='' onClose={() => setShowModal(false)} overlayStyles={tw`bg-[#00000080] `}>
      <Wrapper tw='max-w-[800px] lg:min-w-[800px] bg-neutral-background p-0'>
        <Title tw='text-[16px] text-left p-[12px] border-b border-b-gray-2 w-full'>
          {t('insights.overview_page.trending_section.top_entities.modal_title')}
        </Title>

        <div tw='flex flex-col gap-[10px] px-[12px]'>
          <Header>
            <div tw='flex gap-[8px] items-center flex-1'>
              <DateIntervalSelection resultType='none' tw='px-0 w-auto' />
              <TypeDropdown />
              <div tw='flex items-center bg-transparent h-[28px] px-[12px] rounded text-[14px] font-normal text-text-and-icon'>
                {t('insights.overview_page.date_picker_selection.trending_result', {
                  result: rankedNamedEntities.length.toLocaleString(),
                })}{' '}
              </div>
            </div>

            <SearchBar
              tw='text-text-and-icon'
              inputStyles={tw`h-[26px] placeholder:text-[12px] placeholder:text-text-and-icon-muted placeholder:opacity-50`}
              value={entitiesSearchTerm}
              onChange={setEntitiesSearchTerm}
              placeholder={t(
                'insights.overview_page.trending_section.top_entities.search_placeholder'
              )}
            />
          </Header>

          <NamedEntitiesTable
            totalEpisodesCount={episodesCount}
            namedEntities={currentPageData}
            isLoading={isLoadingNamedEntities}
          />
        </div>

        <Footer tw='justify-between p-[12px] border-t border-t-gray-2'>
          {
            <div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                paginationRange={paginationRange}
                onPageChange={goToPage}
                onNext={goToNextPage}
                onPrevious={goToPreviousPage}
              />
            </div>
          }
          <Button.Primary
            onClick={() => {
              setShowModal(false);
            }}>
            {t('common.close')}
          </Button.Primary>
        </Footer>
      </Wrapper>
    </Modal>
  );

  return (
    <Wrapper>
      <div tw='flex flex-col gap-[10px]'>
        <Header>
          <div tw='flex gap-[8px] items-center'>
            <Title tw='self-center'>
              {t('insights.overview_page.trending_section.top_entities.title')}
            </Title>
            <TypeDropdown />
          </div>

          <SearchBar
            tw='text-text-and-icon'
            inputStyles={tw`h-[26px] placeholder:text-[12px] placeholder:text-text-and-icon-muted placeholder:opacity-50`}
            value={entitiesSearchTerm}
            onChange={setEntitiesSearchTerm}
            placeholder={t(
              'insights.overview_page.trending_section.top_entities.search_placeholder'
            )}
          />
        </Header>

        <NamedEntitiesTable
          totalEpisodesCount={episodesCount}
          namedEntities={top10NamedEntities}
          isLoading={isLoadingNamedEntities}
        />
      </div>

      <Footer>
        <SeeMoreButton
          onClick={() => {
            setShowModal(true);
          }}>
          {t('insights.overview_page.trending_section.top_entities.see_more')}
        </SeeMoreButton>
      </Footer>

      {showModal && CategoriesModal}
    </Wrapper>
  );
};

type NamedEntitiesTableProps = {
  totalEpisodesCount: number;
  namedEntities: RankedNamedEntitySummary[];
  isLoading: boolean;
};

const SeeEpisodesButton = ({
  entity,
  selectedDateRange,
}: {
  entity: RankedNamedEntitySummary;
  selectedDateRange: DateRange;
}) => {
  const handleClick = () => {
    const token = objectToToken({
      all: [
        new AQLLinkedNamedEntitiesQuery('includes', [
          new AQLLinkedNamedEntitiesFilterQuery('id', 'eq', entity.id),
        ]),
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate || '',
          lte: selectedDateRange.maxDate || '',
        }),
      ],
    });
    const url = `/insights/episodes?query_token=${token}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <SeeEpisodesButtonWrapper onClick={handleClick} title='See Episodes'>
      <OpenInNewTabIcon width={14} height={14} />
    </SeeEpisodesButtonWrapper>
  );
};

const NamedEntitiesTable = ({
  totalEpisodesCount,
  namedEntities,
  isLoading,
}: NamedEntitiesTableProps) => {
  const { t } = useTranslation();
  const { selectedDateRange } = useOverviewContext();

  return (
    <Table>
      <TableHeader tw='grid-cols-[30px, 3fr, 2fr, 1fr, 1fr, 40px]'>
        <TableHeaderCell>
          {t('insights.overview_page.trending_section.top_entities.table.rank')}
        </TableHeaderCell>
        <TableHeaderCell>
          {t('insights.overview_page.trending_section.top_entities.table.entities')}
        </TableHeaderCell>
        <TableHeaderCell>
          {t('insights.overview_page.trending_section.top_entities.table.type')}
        </TableHeaderCell>
        <TableHeaderCell tw='justify-end'>
          {t('insights.overview_page.trending_section.top_entities.table.number_of_episodes')}
        </TableHeaderCell>
        <TableHeaderCell tw='justify-end'>
          {t('insights.overview_page.trending_section.top_entities.table.percent_of_content')}
        </TableHeaderCell>
      </TableHeader>

      {isLoading ? (
        <Loader size='md' theme='dark' />
      ) : (
        <TableBody>
          {namedEntities?.map((entity) => (
            <TableBodyRow
              key={`${entity.originalIndex}-${entity.value}-${entity.type}`}
              tw='grid-cols-[30px, 3fr, 2fr, 1fr, 1fr, 40px]'>
              <TableBodyCell>{entity.originalIndex + 1}</TableBodyCell>
              <TableBodyCell>{entity.value}</TableBodyCell>
              <TableBodyCell title={entity.typeName}>
                <div tw='truncate'>{entity.typeName}</div>
              </TableBodyCell>
              <TableBodyCell tw='justify-end'>{entity.audio_count.toLocaleString()}</TableBodyCell>
              <TableBodyCell tw='justify-end'>
                {((entity.audio_count / totalEpisodesCount) * 100).toFixed(1)}%
              </TableBodyCell>

              <SeeEpisodesButton entity={entity} selectedDateRange={selectedDateRange} />
            </TableBodyRow>
          ))}

          {!namedEntities?.length && (
            <NoDataAvailable>
              {' '}
              {t('insights.overview_page.trending_section.top_entities.table.no_data')}{' '}
            </NoDataAvailable>
          )}
        </TableBody>
      )}
    </Table>
  );
};
